import React, { useMemo } from "react";

import { NumberParam, useQueryParams } from "use-query-params";
import { ArrowLeftIcon, ArrowRightIcon } from "../icons";
import useTranslations from "../../hooks/useTranslations";

type PaginationProps = {
  itemsCount: number;
  itemsPerPage: number;
  defaultPage?: number;
};

const Pagination: React.FC<PaginationProps> = ({
  itemsCount,
  itemsPerPage,
  defaultPage = 1,
}) => {
  const t = useTranslations();
  const pageCount = useMemo(() => Math.ceil(itemsCount / itemsPerPage), [
    itemsCount,
    itemsPerPage,
  ]);
  const [{ page }, setQuery] = useQueryParams({
    page: NumberParam,
  });

  const currentPage = page || defaultPage;

  const activeIndexes: boolean[] = useMemo(() => {
    const activeIndexes = new Array(pageCount).fill(false).map((_, index) => {
      if (index < 3) return true;
      if (index >= currentPage - 2 && index <= currentPage) return true;
      if (index > pageCount - 4) return true;
      return false;
    });

    return activeIndexes;
  }, [currentPage, pageCount]);

  return (
    <nav className="flex items-center justify-between px-4 pb-4 bg-white border-t border-gray-200 rounded-b sm:px-0">
      <div className="flex flex-1 w-0 -mt-px">
        <button
          role=""
          onClick={() => setQuery({ page: currentPage - 1 })}
          className="inline-flex items-center pt-4 pr-1 text-sm font-medium text-gray-500 border-t-2 border-transparent disabled:cursor-not-allowed focus:outline-none hover:text-gray-700 hover:border-gray-300"
          aria-current="page"
          disabled={currentPage - 1 <= 0}
          id="previousButton"
        >
          <ArrowLeftIcon iconClassName="w-5 h-5 mr-3 text-gray-400" />
          {t("forms.actions.previous")}
        </button>
      </div>
      <div className="hidden md:-mt-px md:flex">
        {activeIndexes.map((_, index) => {
          if (activeIndexes[index])
            return (
              <button
                role=""
                onClick={() => setQuery({ page: index + 1 })}
                key={index}
                className={`inline-flex focus:outline-none items-center px-4 pt-4 text-sm font-medium border-t-2 ${
                  index === currentPage - 1
                    ? "text-primary-600 border-primary-500"
                    : "text-light-600 border-light-500"
                } hover:text-primary-600 hover:border-primary-500`}
                aria-current="page"
                id={`goToPage${index}Button`}
              >
                {index + 1}
              </button>
            );
          else if (!activeIndexes[index] && activeIndexes?.[index - 1])
            return (
              <span className="inline-flex items-center px-4 pt-4 text-sm font-medium text-gray-500 border-t-2 border-transparent">
                ...
              </span>
            );
        })}
      </div>
      <div className="flex justify-end flex-1 w-0 -mt-px">
        <button
          disabled={currentPage + 1 > pageCount}
          onClick={() => setQuery({ page: currentPage + 1 })}
          className="inline-flex items-center pt-4 pl-1 text-sm font-medium text-gray-500 border-t-2 border-transparent disabled:cursor-not-allowed focus:outline-none hover:text-gray-700 hover:border-gray-300"
          id="nextButton"
        >
          {t("forms.actions.next")}
          <ArrowRightIcon iconClassName="w-5 h-5 ml-3 text-gray-400" />
        </button>
      </div>
    </nav>
  );
};

export default React.memo(Pagination);
